/* ═════════════════════════════════════════════════════════════════════════════
   SRC/COMPONENTS/TABCOOPERACAOVIEW/DATATRANSFORMS.JS — funções puras de dados
   ─────────────────────────────────────────────────────────────────────────────
   O QUE FAZ
   Filtragem, agregação e pequenos "hashes" determinísticos (sem React, sem
   Redux, sem rede) usados pelo feed CooperAção (./index.jsx) pra: filtrar
   planos compartilhados por etapa/driver/busca, escolher os "curtidores"
   exibidos em cada post (efeito "Curtido por X, Y e outros N", estável por
   id do post) e calcular os números agregados do header do feed (ganho
   total, nº de Unimeds, estatísticas por Unimed). Extraídas do componente
   pra poderem ser testadas isoladamente — mesmo padrão de
   tabTrendsView/dataTransforms.js e tabSegmentationView/dataTransforms.js.

   USADO POR
   ./index.jsx (CooperacaoView).
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Filtra os planos compartilhados do feed por etapa/indicador, por conjunto
 * de drivers (o plano precisa ter PELO MENOS um dos drivers filtrados) e por
 * busca textual (título + descrição + Unimed + tags, case-insensitive).
 * A busca, quando preenchida, é aplicada por cima do filtro de etapa/driver
 * (mesma precedência de curto-circuito do componente original).
 */
export function filterPlanos(
  planos,
  { etapaFilter = "todas", driverFilter = new Set(), busca = "" } = {},
) {
  return planos.filter((p) => {
    if (etapaFilter !== "todas" && p.etapa !== etapaFilter) return false;
    if (driverFilter.size > 0) {
      const planDrivers = p.drivers || [];
      if (!planDrivers.some((d) => driverFilter.has(d))) return false;
    }
    if (busca.trim()) {
      const b = busca.toLowerCase();
      return (
        p.titulo + " " + p.desc + " " + p.unimed + " " + (p.tags || []).join(" ")
      )
        .toLowerCase()
        .includes(b);
    }
    return true;
  });
}

// Pool de "curtidores" pra efeito Instagram ("Curtido por X, Y e outros N")
export const CURTIDORES_POOL = [
  { nome: "Mariana Costa", unimed: "Unimed Goiânia" },
  { nome: "Roberto Tanaka", unimed: "Unimed Curitiba" },
  { nome: "Helena Ribeiro", unimed: "Unimed Brasília" },
  { nome: "Marcos Aurélio", unimed: "Unimed Brasília" },
  { nome: "Tânia Oliveira", unimed: "Unimed Santos" },
  { nome: "Paulo Almeida", unimed: "Unimed Ribeirão" },
  { nome: "Cristina Souza", unimed: "Unimed Uberlândia" },
  { nome: "Carlos Drummond", unimed: "Unimed Belo Horizonte" },
  { nome: "Beatriz Lopes", unimed: "Unimed Florianópolis" },
  { nome: "Felipe Andrade", unimed: "Unimed Maceió" },
  { nome: "Patrícia Vargas", unimed: "Unimed Manaus" },
  { nome: "Eduardo Nunes", unimed: "Unimed Natal" },
  { nome: "Joana Bittencourt", unimed: "Unimed João Pessoa" },
  { nome: "Rafael Moura", unimed: "Unimed Aracaju" },
  { nome: "Vanessa Lins", unimed: "Unimed Teresina" },
  { nome: "Sérgio Bastos", unimed: "Unimed Cuiabá" },
  { nome: "Letícia Marquez", unimed: "Unimed Campo Grande" },
  { nome: "Bruno Pacheco", unimed: "Unimed Vitória" },
  { nome: "Gabriela Saldanha", unimed: "Unimed São Luís" },
  { nome: "Henrique Albuquerque", unimed: "Unimed Palmas" },
  { nome: "Renato Andrade", unimed: "Unimed" },
  { nome: "Camila Pereira", unimed: "Unimed Sorocaba" },
];

/**
 * Pega 2 "curtidores" estáveis por post (hash determinístico do id, mesmo
 * post sempre mostra as mesmas pessoas). Se o hash colidir nos dois índices
 * (mesma pessoa duas vezes), desempata avançando pro próximo índice do pool
 * em vez de mostrar o mesmo nome repetido.
 */
export function curtidoresDoPost(id, pool = CURTIDORES_POOL) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const a = pool[h % pool.length];
  const b = pool[(h * 7 + 13) % pool.length];
  return a.nome === b.nome ? [a, pool[(h + 1) % pool.length]] : [a, b];
}

/**
 * Estatísticas agregadas do feed: ganho total em pp, nº de Unimeds
 * distintas que postaram planos, e por Unimed — planos postados e
 * "realizados" (mock determinístico: postados + offset estável derivado de
 * hash do nome, exceto a Unimed atual, que usa o nº real de ações internas
 * concluídas/postadas).
 */
export function computeCoopStats(planos, acoes, unimedAtual) {
  const totalGanho = planos.reduce((s, p) => s + p.ganhoPp, 0);
  const totalUnimeds = new Set(planos.map((p) => p.unimed)).size;

  const acoesConcluidasInternas = acoes.filter(
    (a) => a.status === "concluido",
  ).length;
  const acoesPostadasInternas = planos.filter(
    (p) => p.unimed === unimedAtual,
  ).length;

  const unimedStats = {};
  planos.forEach((p) => {
    if (!unimedStats[p.unimed]) {
      let h = 0;
      for (let i = 0; i < p.unimed.length; i++)
        h = (h * 31 + p.unimed.charCodeAt(i)) >>> 0;
      unimedStats[p.unimed] = {
        postados: 0,
        realizadosOffset: 3 + (h % 7),
        totalGanho: 0,
      };
    }
    unimedStats[p.unimed].postados += 1;
    unimedStats[p.unimed].totalGanho += p.ganhoPp || 0;
  });
  Object.keys(unimedStats).forEach((u) => {
    if (u === unimedAtual) {
      unimedStats[u].realizados = acoesConcluidasInternas;
      unimedStats[u].postados = acoesPostadasInternas;
    } else {
      unimedStats[u].realizados =
        unimedStats[u].postados + unimedStats[u].realizadosOffset;
    }
  });

  return { totalGanho, totalUnimeds, unimedStats };
}
